// src/components/legal/TermsOfService.jsx
// Full-screen modal overlay for the Terms of Service.
// Usage: <TermsOfService onClose={() => setShow(false)} />

export default function TermsOfService({ onClose }) {
  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()} role="presentation">
      <div style={styles.sheet} role="dialog" aria-modal="true" aria-label="Terms of Service">
        <div style={styles.header}>
          <span style={styles.title}>Terms of Service</span>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={styles.body}>
          <p style={styles.meta}>Last updated: March 2026</p>

          <Section title="1. Acceptance of Terms">
            <p>By creating an account or using Orbit, you agree to these Terms of Service ("Terms"). If you do not agree, do not use Orbit. We may update these Terms from time to time; continued use after changes constitutes acceptance.</p>
          </Section>

          <Section title="2. Description of Service">
            <p>Orbit is a personal scheduling and relationship management application that aggregates calendar data from third-party providers, tracks social fairness metrics, and facilitates scheduling with people in your network. Orbit is provided "as is" and is currently in active development.</p>
          </Section>

          <Section title="3. Eligibility">
            <p>You must be at least 13 years old to use Orbit. By using Orbit you represent that you meet this requirement. If you are under 18, you represent that a parent or guardian has reviewed and agreed to these Terms on your behalf.</p>
          </Section>

          <Section title="4. Your Account">
            <p>You are responsible for keeping your login credentials secure. You are responsible for all activity that occurs under your account. Notify us immediately at <strong>support@orbitapp.io</strong> if you suspect unauthorised access.</p>
            <p>You may not create accounts using automated means, create multiple accounts for abusive purposes, or impersonate others.</p>
          </Section>

          <Section title="5. Third-Party Calendar Integrations">
            <p>When you connect a third-party calendar (Google, Microsoft, Apple, Yahoo, etc.), you authorise Orbit to access your calendar data through that provider's API in accordance with the permissions you grant. Your use of those integrations is also governed by the third party's own terms of service. Orbit does not modify, delete, or share your calendar data with other users without your explicit action.</p>
          </Section>

          <Section title="6. Acceptable Use">
            <p>You agree not to:</p>
            <ul>
              <li>Use Orbit to harass, threaten, or harm others.</li>
              <li>Attempt to gain unauthorised access to Orbit's systems or another user's data.</li>
              <li>Use automated scripts or bots to access the service in a way that places unreasonable load on our infrastructure.</li>
              <li>Violate any applicable law or regulation.</li>
              <li>Reverse-engineer, decompile, or disassemble any part of Orbit.</li>
            </ul>
          </Section>

          <Section title="7. User Content">
            <p>You retain ownership of the data you input into Orbit (contact names, relationship details, scheduling messages, etc.). By using Orbit you grant us a limited licence to store and process that data solely to provide the service to you. We do not claim ownership of your data.</p>
          </Section>

          <Section title="8. Notifications">
            <p>If you enable email or SMS notifications, you consent to receive those messages from Orbit. You can disable notifications at any time in Settings → Notifications. Standard message and data rates may apply for SMS.</p>
          </Section>

          <Section title="9. Availability and Modifications">
            <p>We strive to keep Orbit available but do not guarantee uninterrupted access. We may modify, suspend, or discontinue features at any time. We will provide reasonable advance notice of significant changes where possible.</p>
          </Section>

          <Section title="10. Disclaimer of Warranties">
            <p>Orbit is provided "as is" without warranty of any kind, express or implied, including warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the service will be error-free or that calendar data will be complete or accurate — third-party API availability and data accuracy are outside our control.</p>
          </Section>

          <Section title="11. Limitation of Liability">
            <p>To the fullest extent permitted by law, Orbit and its operators shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the service, including loss of data, missed appointments, or reliance on scheduling information. Our total liability for direct damages shall not exceed the amount you paid us in the 12 months preceding the claim (which may be zero for a free service).</p>
          </Section>

          <Section title="12. Indemnification">
            <p>You agree to indemnify and hold harmless Orbit and its operators from any claims, damages, or expenses (including reasonable legal fees) arising from your use of the service, your violation of these Terms, or your violation of any third party's rights.</p>
          </Section>

          <Section title="13. Governing Law">
            <p>These Terms are governed by the laws of the jurisdiction in which Orbit's operator is registered, without regard to conflict-of-law principles. Any disputes shall be resolved in the courts of that jurisdiction.</p>
          </Section>

          <Section title="14. Termination">
            <p>We may suspend or terminate your account if you violate these Terms. You may delete your account at any time by contacting us at <strong>support@orbitapp.io</strong>. Upon termination, your data will be deleted in accordance with our Privacy Policy.</p>
          </Section>

          <Section title="15. Contact">
            <p>Questions about these Terms can be sent to: <strong>legal@orbitapp.io</strong></p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: "#4A3020", marginBottom: 8, marginTop: 0 }}>{title}</h3>
      <div style={{ fontSize: 13, color: "#5A4535", lineHeight: 1.65 }}>{children}</div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(30,16,8,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 16,
    paddingTop: "max(16px, env(safe-area-inset-top, 16px))",
    overflowY: "auto",
  },
  sheet: {
    width: "100%", maxWidth: 640,
    background: "#FFFCF8",
    border: "1px solid #E4D8C8",
    borderRadius: 18,
    boxShadow: "0 16px 64px rgba(60,40,20,.2)",
    maxHeight: "90vh",
    display: "flex", flexDirection: "column",
    fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid #E4D8C8",
    background: "#FDF8F2",
    borderRadius: "18px 18px 0 0",
    flexShrink: 0,
  },
  title: {
    fontSize: 17, fontFamily: "'Crimson Pro', serif", fontWeight: 600, color: "#1E1008",
  },
  closeBtn: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 16, color: "#A08870", padding: "4px 8px", borderRadius: 8,
  },
  body: {
    overflowY: "auto",
    padding: "20px 24px 28px",
  },
  meta: {
    fontSize: 12, color: "#A08870", marginBottom: 20, marginTop: 0,
  },
};
